import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { createLogger } from '~/utils/logging';
import { useHttpClient } from '~/hooks/useHttpClient';
import { PERIOD_LIST_ALL_QUERY_KEY, PERIOD_QUERY_KEY } from '~/services/period';
import type { Period } from '~/services/types';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { sortPeriodsByShortName } from '~/utils/period';

type PeriodRecord = Record<string, unknown> & { id: string };
type PeriodListData = { periods: PeriodRecord[] };

const log = createLogger('usePeriodRestoreMutation');

export type RestorePeriodRequest = {
  periodId: string;
};

export type RestorePeriodResponse = {
  period: Period;
};

export function usePeriodRestoreMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: RestorePeriodRequest) => {
      log.debug('Restoring period', { request });
      const { period } = await httpClient.post<RestorePeriodResponse>('/period/restore', request);
      if (period) {
        log.info('Restored period successfully', { periodId: period.id });
      }
      return { period };
    },
    onMutate: async (request: RestorePeriodRequest) => {
      const { periodId } = request;

      // Cancel any outgoing refetches to prevent them from overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: [PERIOD_LIST_ALL_QUERY_KEY] });
      await queryClient.cancelQueries({ queryKey: [PERIOD_QUERY_KEY, periodId] });

      // Snapshot the previous values
      const previousActivePeriodsWithStudents = queryClient.getQueryData([PERIOD_LIST_ALL_QUERY_KEY, true, false]);
      const previousArchivedPeriodsWithStudents = queryClient.getQueryData([PERIOD_LIST_ALL_QUERY_KEY, true, true]);
      const previousPeriodWithStudents = queryClient.getQueryData([PERIOD_QUERY_KEY, periodId, true]);

      // Optimistically update active periods list (add the period with archivedAt: null)
      queryClient.setQueryData<PeriodListData>([PERIOD_LIST_ALL_QUERY_KEY, true, false], (old) => {
        if (!old?.periods) return old;

        // Find the period in the current list or from the archived list snapshot
        const periodToRestore =
          old.periods.find((p) => p.id === periodId) ||
          (previousArchivedPeriodsWithStudents as PeriodListData | undefined)?.periods?.find((p) => p.id === periodId);

        if (!periodToRestore) return old;

        // Check if period already exists in active list
        const existsInActive = old.periods.some((p) => p.id === periodId);

        if (existsInActive) {
          // Update existing period
          return {
            ...old,
            periods: old.periods.map((p) => (p.id === periodId ? { ...p, archivedAt: null } : p)),
          };
        } else {
          // Add period to active list
          return {
            ...old,
            periods: sortPeriodsByShortName([...old.periods, { ...periodToRestore, archivedAt: null }] as Period[]),
          };
        }
      });

      // Optimistically update single period query (set archivedAt)
      queryClient.setQueryData<PeriodRecord>([PERIOD_QUERY_KEY, periodId, true], (old) => {
        if (!old) return old;
        return { ...old, archivedAt: null };
      });

      // Return context object with snapshotted values for rollback
      return {
        previousActivePeriodsWithStudents,
        previousArchivedPeriodsWithStudents,
        previousPeriodWithStudents,
        periodId,
      };
    },
    onError: (error: Error, _request, context) => {
      // Rollback optimistic updates on error
      if (context) {
        queryClient.setQueryData([PERIOD_LIST_ALL_QUERY_KEY, true, false], context.previousActivePeriodsWithStudents);
        queryClient.setQueryData([PERIOD_LIST_ALL_QUERY_KEY, true, true], context.previousArchivedPeriodsWithStudents);
        queryClient.setQueryData([PERIOD_QUERY_KEY, context.periodId, true], context.previousPeriodWithStudents);
      }

      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to restore period', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
    onSettled: (_data, _error, request) => {
      // Always refetch after error or success to ensure server state is correct
      queryClient.invalidateQueries({ queryKey: [PERIOD_LIST_ALL_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PERIOD_QUERY_KEY, request.periodId] });
    },
  });

  return {
    data,
    error,
    mutate,
    reset,
    status,
  };
}
